/**
 * Version Check Hook
 * Checks the server build version on page load against localStorage.
 * If a new deploy happened since last visit, shows a banner prompting refresh.
 *
 * Web-only — mobile apps update through app stores.
 */

import { API_ENDPOINTS, apiUrl } from '@config/api';
import { useCallback, useEffect, useState } from 'react';

const VERSION_STORAGE_KEY = 'app_version';

interface VersionCheckResult {
  /** True when the server version differs from the last stored version */
  hasNewVersion: boolean;
  /** Dismiss the banner until next page load */
  dismiss: () => void;
}

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch(apiUrl(API_ENDPOINTS.CONFIG_VERSION));
    if (!res.ok) return null;
    const data = (await res.json()) as { version: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}

export function useVersionCheck(): VersionCheckResult {
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  useEffect(() => {
    // Skip version check in development — local server version changes on every restart
    if (import.meta.env.DEV) return;

    let mounted = true;

    fetchVersion().then((serverVersion) => {
      if (!mounted || !serverVersion) return;

      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

      // Always store the latest version
      localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);

      // Show banner only if we had a previous version and it differs
      if (storedVersion && storedVersion !== serverVersion) {
        setHasNewVersion(true);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  return {
    hasNewVersion: hasNewVersion && !dismissed,
    dismiss,
  };
}
